// filepath: admin-dashboard/src/components/layout/Sidebar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';
import { useProject, ProjectId } from '../../context/ProjectContext';
import {
  CollectionDef,
  getCollectionsForProject,
  PROJECT_LABELS,
  collectionSectionId,
} from '../../config/collectionRegistry';
import { pageConfigApi } from '../../services/api/pageConfigApi';

const PROJECT_OPTIONS: ProjectId[] = ['admiral', 'bolt-jr'];

/**
 * Reorder the registry collections to match the user-defined section order
 * saved via the Page Config page. Collections missing from `sectionOrder`
 * (e.g. newly added types) are appended in their registry order so they
 * remain reachable.
 */
function applySectionOrder(
  collections: CollectionDef[],
  sectionIds: string[],
): CollectionDef[] {
  if (sectionIds.length === 0) return collections;
  const byId = new Map<string, CollectionDef>();
  for (const c of collections) byId.set(collectionSectionId(c), c);
  const ordered: CollectionDef[] = [];
  const used = new Set<string>();
  for (const id of sectionIds) {
    const c = byId.get(id);
    if (c && !used.has(id)) {
      ordered.push(c);
      used.add(id);
    }
  }
  for (const c of collections) {
    if (!used.has(collectionSectionId(c))) ordered.push(c);
  }
  return ordered;
}

export const Sidebar: React.FC = () => {
  const { isAdmin } = useAuth();
  const { project, setProject } = useProject();
  const { locale } = useLocale();
  const navigate = useNavigate();

  const registryCollections = useMemo(
    () => getCollectionsForProject(project),
    [project],
  );
  const [sectionIds, setSectionIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      pageConfigApi
        .get(project, locale)
        .then((cfg) => {
          if (!cancelled) {
            setSectionIds(cfg.sectionOrder.map((s) => s.id));
          }
        })
        .catch(() => {
          if (!cancelled) setSectionIds([]);
        });
    };
    load();
    // Refresh whenever the Page Config page saves a new order.
    const onUpdated = () => load();
    window.addEventListener('pageconfig-updated', onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('pageconfig-updated', onUpdated);
    };
  }, [project, locale]);

  const collections = useMemo(
    () => applySectionOrder(registryCollections, sectionIds),
    [registryCollections, sectionIds],
  );

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProject(e.target.value as ProjectId);
    navigate('/');
  };

  return (
    <aside className="bg-gray-800 text-white w-64 min-h-screen p-4">
      <h1 className="text-2xl font-bold mb-4">
        <NavLink to="/" className="hover:text-gray-300">
          Admin Dashboard
        </NavLink>
      </h1>

      {/* Project selector */}
      <div className="mb-6">
        <label htmlFor="project-select" className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
          Project
        </label>
        <select
          id="project-select"
          value={project}
          onChange={handleProjectChange}
          className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {PROJECT_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {PROJECT_LABELS[p]}
            </option>
          ))}
        </select>
      </div>

      <nav>
        <ul>
          {/* Content section label */}
          <li className="mb-2 px-2 text-xs uppercase tracking-wider text-gray-400">
            Content
          </li>

          {collections.map(({ name, path }) => (
            <li key={path} className="mb-4">
              <NavLink
                to={path}
                className={({ isActive }) =>
                  `block px-2 py-1 rounded hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`
                }
              >
                {name}
              </NavLink>
            </li>
          ))}

          {/* Layout — typography + section order, scoped to current project */}
          <li className="mt-6 mb-2 px-2 text-xs uppercase tracking-wider text-gray-400">
            Layout
          </li>
          <li className="mb-4">
            <NavLink
              to="/page-config"
              className={({ isActive }) =>
                `block px-2 py-1 rounded hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`
              }
            >
              Typography &amp; sections
            </NavLink>
          </li>

          {/* Admin-only section */}
          {isAdmin && (
            <>
              <li className="mt-6 mb-2 px-2 text-xs uppercase tracking-wider text-gray-400">
                Admin
              </li>
              <li className="mb-4">
                <NavLink
                  to="/users"
                  className={({ isActive }) =>
                    `block px-2 py-1 rounded hover:bg-gray-700 ${isActive ? 'bg-gray-700' : ''}`
                  }
                >
                  Users
                </NavLink>
              </li>
            </>
          )}
        </ul>
      </nav>
    </aside>
  );
};
